if (!customElements.get('product-pairing')) {
  customElements.define(
    'product-pairing',
    class ProductPairing extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.currentVariantId = Number(this.dataset.currentVariantId);
        this.currentVariantPrice = Number(this.dataset.currentVariantPrice);
        this.pairedVariantId = Number(this.dataset.pairedVariantId);
        this.pairedVariantPrice = Number(this.dataset.pairedVariantPrice);
        this.moneyFormat = this.dataset.moneyFormat;

        this.button = this.querySelector('[data-add-both]');
        this.ctaText = this.querySelector('[data-pairing-cta-text]');
        this.spinner = this.querySelector('.loading__spinner');
        this.errorEl = this.querySelector('[data-pairing-error]');
        this.currentPriceEl = this.querySelector('[data-current-price]');

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.button?.addEventListener('click', this.addBoth.bind(this));

        this.unsubscribeVariantChange = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this),
        );
      }

      disconnectedCallback() {
        this.unsubscribeVariantChange?.();
      }

      onVariantChange({ data }) {
        if (data.sectionId !== this.sectionId) return;

        const variant = data.variant;
        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.currentVariantId = variant.id;
        this.currentVariantPrice = variant.price;
        if (this.currentPriceEl) {
          this.currentPriceEl.textContent = this.formatMoney(variant.price);
        }
        this.updateCtaText();
        this.setAvailable();
      }

      updateCtaText() {
        if (!this.ctaText) return;
        const combined = this.currentVariantPrice + this.pairedVariantPrice;
        this.ctaText.textContent = `Buy both for ${this.formatMoney(combined)}`;
      }

      // No matching variant for the current selection: disable the button
      // and blank the stale price/CTA rather than leave the previous
      // variant's numbers visible next to a disabled control.
      setUnavailable() {
        this.button?.setAttribute('aria-disabled', 'true');
        this.currentPriceEl?.classList.add('hidden');
        this.ctaText?.classList.add('hidden');
      }

      setAvailable() {
        this.button?.removeAttribute('aria-disabled');
        this.currentPriceEl?.classList.remove('hidden');
        this.ctaText?.classList.remove('hidden');
      }

      async addBoth(evt) {
        evt.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        this.button.setAttribute('aria-disabled', 'true');
        this.button.classList.add('loading');
        this.spinner?.classList.remove('hidden');
        this.hideError();

        const sections =
          this.cart?.getSectionsToRender().map((s) => s.id) ?? [];

        try {
          const response = await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [
                { id: this.currentVariantId, quantity: 1 },
                { id: this.pairedVariantId, quantity: 1 },
              ],
              sections,
              sections_url: window.location.pathname,
            }),
          }).then((r) => r.json());

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-pairing',
              productVariantId: this.currentVariantId,
              pairedVariantId: this.pairedVariantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.showError(response.description || response.message);
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-pairing',
            variantId: this.currentVariantId,
            pairedVariantId: this.pairedVariantId,
            cartData: response,
          });

          if (this.cart) {
            // Multi-item /cart/add.js responses have no top-level `key`
            // (cart-notification.js expects one to target the item it just
            // added). Point it at the current product's line, not the
            // paired one, so the notification shows the item the shopper
            // was actually looking at rather than making it look like that
            // one didn't get added.
            response.key = response.items?.[0]?.key;
            this.cart.setActiveElement?.(document.activeElement);
            this.cart.renderContents(response);
            this.cart.classList.remove('is-empty');
          } else {
            window.location = routes.cart_url;
          }
        } catch (error) {
          console.error('Error adding pairing to cart:', error);
          this.showError('Something went wrong. Please try again.');
        } finally {
          this.button.classList.remove('loading');
          this.spinner?.classList.add('hidden');
          this.button.removeAttribute('aria-disabled');
        }
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent =
          message || 'Something went wrong. Please try again.';
        this.errorEl.hidden = false;
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
      }

      // Port of Shopify's money_format token replacement (see Shopify's
      // public money.js reference implementation) so this works without
      // depending on Shopify.formatMoney being loaded on the page.
      formatMoney(cents) {
        const format = this.moneyFormat || '${{amount}}';

        const withDelimiters = (precision, thousands, decimal) => {
          const value = (cents / 100).toFixed(precision);
          const [whole, fraction] = value.split('.');
          const groupedWhole = whole.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            thousands,
          );
          return fraction
            ? `${groupedWhole}${decimal}${fraction}`
            : groupedWhole;
        };

        return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
          switch (token) {
            case 'amount_no_decimals':
              return withDelimiters(0, ',', '.');
            case 'amount_with_comma_separator':
              return withDelimiters(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withDelimiters(0, '.', ',');
            case 'amount_with_space_separator':
              return withDelimiters(2, ' ', ',');
            case 'amount_no_decimals_with_space_separator':
              return withDelimiters(0, ' ', ',');
            case 'amount_with_apostrophe_separator':
              return withDelimiters(2, "'", '.');
            case 'amount_no_decimals_with_apostrophe_separator':
              return withDelimiters(0, "'", '.');
            case 'amount':
            default:
              return withDelimiters(2, ',', '.');
          }
        });
      }
    },
  );
}
